import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

function weekMonday(d = new Date()) {
  const day = d.getDay(); const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.setDate(diff)).toISOString().slice(0, 10);
}

function statusFor(kpi, val) {
  if (val === undefined || val === null) return "none";
  if (kpi.direction === "higher") return val >= kpi.target ? "green" : val >= kpi.target * 0.9 ? "yellow" : "red";
  return val <= kpi.target ? "green" : val <= kpi.target * 1.1 ? "yellow" : "red";
}
const dot = { green: "bg-green-600", yellow: "bg-yellow-500", red: "bg-red-600", none: "bg-neutral-300" };

export default function Scorecard() {
  const [kpis, setKpis] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", owner: "", target: 0, unit: "", direction: "higher" });

  const weeks = [...Array(8)].map((_, i) => {
    const d = new Date(); d.setDate(d.getDate() - i * 7);
    return weekMonday(d);
  }).reverse();

  const load = () => api.get("/kpis").then((r) => setKpis(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    await api.post("/kpis", { ...form, target: Number(form.target) });
    setOpen(false); setForm({ name: "", owner: "", target: 0, unit: "", direction: "higher" });
    toast.success("KPI added.");
    load();
  };

  const setValue = async (kpi, weekOf, valStr) => {
    const value = Number(valStr);
    if (Number.isNaN(value)) return;
    await api.post(`/kpis/${kpi.id}/entries`, { week_of: weekOf, value });
    load();
  };

  const remove = async (id) => {
    if (!confirm("Delete this KPI and its history?")) return;
    await api.delete(`/kpis/${id}`); load();
  };

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-start justify-between">
        <div>
          <div className="mono text-xs uppercase tracking-widest text-neutral-500">Module · Scorecard</div>
          <h1 className="font-display text-4xl font-bold mt-2">Weekly Scorecard</h1>
          <p className="text-neutral-600 mt-2">The 5–15 numbers that tell you if the business is on track.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#E54D2E] hover:bg-[#C93E22] text-white rounded-sm btn-lift" data-testid="scorecard-add-kpi-btn"><Plus className="h-4 w-4 mr-1" /> Add KPI</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New KPI</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Name</Label><Input value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} className="rounded-sm" data-testid="kpi-name-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Owner</Label><Input value={form.owner} onChange={(e)=>setForm({...form,owner:e.target.value})} className="rounded-sm" data-testid="kpi-owner-input" /></div>
                <div><Label>Unit (e.g. $, %, #)</Label><Input value={form.unit} onChange={(e)=>setForm({...form,unit:e.target.value})} className="rounded-sm" data-testid="kpi-unit-input" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Weekly target</Label><Input type="number" value={form.target} onChange={(e)=>setForm({...form,target:e.target.value})} className="rounded-sm" data-testid="kpi-target-input" /></div>
                <div>
                  <Label>Direction</Label>
                  <Select value={form.direction} onValueChange={(v)=>setForm({...form,direction:v})}>
                    <SelectTrigger className="rounded-sm" data-testid="kpi-direction-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="higher">Higher is better</SelectItem>
                      <SelectItem value="lower">Lower is better</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button onClick={create} className="w-full bg-neutral-900 hover:bg-neutral-800 rounded-sm" data-testid="kpi-create-submit-btn">Create KPI</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-8 border border-neutral-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-neutral-200 bg-neutral-50">
              <th className="text-left px-4 py-3 mono text-[10px] uppercase text-neutral-500 tracking-widest sticky left-0 bg-neutral-50">Metric</th>
              <th className="text-left px-4 py-3 mono text-[10px] uppercase text-neutral-500 tracking-widest">Owner</th>
              <th className="text-right px-4 py-3 mono text-[10px] uppercase text-neutral-500 tracking-widest">Goal</th>
              {weeks.map((w) => (
                <th key={w} className="text-right px-3 py-3 mono text-[10px] uppercase text-neutral-500 tracking-widest whitespace-nowrap">{w.slice(5)}</th>
              ))}
              <th className="px-2"></th>
            </tr>
          </thead>
          <tbody>
            {kpis.length === 0 && (
              <tr><td colSpan={weeks.length + 4} className="text-center py-16 text-neutral-500">No KPIs yet. Add your first metric →</td></tr>
            )}
            {kpis.map((k) => {
              const entriesMap = Object.fromEntries((k.entries || []).map((e) => [e.week_of, e.value]));
              return (
                <tr key={k.id} className="border-b border-neutral-100 hover:bg-neutral-50" data-testid={`kpi-row-${k.id}`}>
                  <td className="px-4 py-2 font-medium sticky left-0 bg-white">{k.name}</td>
                  <td className="px-4 py-2 text-neutral-600">{k.owner || "—"}</td>
                  <td className="px-4 py-2 mono text-right">{k.direction === "higher" ? "≥" : "≤"} {k.target}{k.unit}</td>
                  {weeks.map((w) => {
                    const v = entriesMap[w];
                    const s = statusFor(k, v);
                    return (
                      <td key={w} className="px-2 py-1">
                        <div className="flex items-center justify-end gap-1.5">
                          <span className={`w-1.5 h-1.5 ${dot[s]}`} />
                          <input
                            defaultValue={v ?? ""}
                            onBlur={(e) => e.target.value && setValue(k, w, e.target.value)}
                            className="mono w-16 text-right bg-transparent border-0 focus:outline-none focus:ring-1 focus:ring-neutral-900 rounded-sm px-1"
                            data-testid={`kpi-cell-${k.id}-${w}`}
                          />
                        </div>
                      </td>
                    );
                  })}
                  <td className="px-2"><button onClick={() => remove(k.id)} className="text-neutral-400 hover:text-red-600" data-testid={`kpi-delete-${k.id}`}><Trash2 className="h-3.5 w-3.5" /></button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
